<template>
  <div
    ref="dropdown"
    class="filter-field"
    :class="{ active: isOpen }"
    data-lenis-prevent>
    <button
      class="filter-trigger"
      type="button"
      :aria-expanded="isOpen"
      @click="toggleDropdown">
      <span class="filter-label">{{ label }}</span>
      <span class="filter-value">{{ selectedLabel }}</span>
      <span class="filter-arrow">
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20">
          <path d="m5.5 7.5 4.5 4.5 4.5-4.5" />
        </svg>
      </span>
    </button>

    <SlideUpDown v-model="isOpen">
      <ul class="filter-options">
        <li
          v-for="option in options"
          :key="option.value"
          :class="{ active: option.value === modelValue }">
          <button
            type="button"
            @click="selectOption(option.value)">
            {{ option.label }}
          </button>
        </li>
      </ul>
    </SlideUpDown>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import SlideUpDown from 'vue3-slide-up-down'

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  modelValue: {
    type: String,
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
})

const emit = defineEmits(['update:modelValue'])
const dropdown = ref(null)
const isOpen = ref(false)

const selectedLabel = computed(
  () =>
    props.options.find((option) => option.value === props.modelValue)?.label ||
    props.options[0]?.label ||
    ''
)

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const selectOption = (value) => {
  emit('update:modelValue', value)
  isOpen.value = false
}

const handleDocumentClick = (event) => {
  if (isOpen.value && !dropdown.value?.contains(event.target)) {
    isOpen.value = false
  }
}

const handleKeydown = (event) => {
  if (event.key === 'Escape') isOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick, true)
  window.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick, true)
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style lang="scss" scoped>
.filter-field {
  @apply relative z-10 flex w-full flex-col rounded-[15px] bg-functional transition-all md:w-[230px];

  &.active {
    @apply z-20;

    .filter-arrow svg {
      @apply rotate-180;
    }

    .filter-options {
      @apply md:visible md:pointer-events-auto md:opacity-100;
    }
  }
}

.filter-trigger {
  @apply relative flex min-h-[56px] w-full flex-col items-start justify-center gap-2 rounded-[15px] border-0 bg-transparent p-4 text-left;
}

.filter-label {
  @apply font-FKGrotesk text-[16px] font-normal leading-4 text-balanced;
}

.filter-value {
  @apply max-w-[175px] overflow-hidden whitespace-nowrap font-FKGrotesk text-[16px] font-medium leading-5 text-humble;
}

.filter-arrow {
  @apply pointer-events-none absolute right-4 top-[calc(50%_-_17.5px)] flex h-[35px] w-[35px] items-center justify-center rounded-full bg-white text-progressive;

  svg {
    @apply h-full w-full p-0.5 transition-transform duration-200;

    path {
      fill: none;
      stroke: rgba(19, 211, 119, 1);
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.5;
    }
  }
}

.filter-options {
  @apply z-10 flex max-h-[350px] w-full flex-col overflow-y-auto rounded-b-[15px] bg-white pl-[10px] shadow-[0px_15px_23px_0px_rgba(0,0,0,0.1)] transition-all md:invisible md:pointer-events-none md:absolute md:top-[calc(100%_-_8px)] md:opacity-0;

  &::-webkit-scrollbar {
    @apply w-[10px];
  }

  &::-webkit-scrollbar-track {
    @apply rounded-2xl border-0 bg-transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply rounded-lg border-[3px] border-solid border-transparent bg-[rgba(30,107,92,0.47)] bg-clip-content;
  }

  li {
    @apply flex border-t border-gray_green_trans p-px;

    &:first-child {
      @apply mt-[8px];
    }

    button {
      @apply w-full rounded-[5px] px-[9px] py-1 text-left font-FKGrotesk text-[14px] font-normal leading-5 text-humble transition-colors;

      &:hover,
      &:focus-visible {
        @apply bg-functional;
      }
    }

    &.active button {
      @apply bg-balanced text-functional;
    }
  }
}
</style>
